import React, { useEffect, useMemo, useState } from "react";

import { api } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

import SingleSelectFilter from "@/components/leads/SingleSelectFilter";
import MultiSelectFilter from "@/components/leads/MultiSelectFilter";

function numOrNull(v) {
  const s = String(v ?? "").trim();
  if (!s) return null;
  const cleaned = s.replace(/[^0-9.-]/g, "");
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

export default function EditCommissionProfileDialog({ open, onOpenChange, row, onSaved, existingProfiles = [] }) {
  const { toast } = useToast();
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [uplines, setUplines] = useState([]);

  const [busy, setBusy] = useState(false);

  const [agentId, setAgentId] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [productId, setProductId] = useState("");
  const [issueAgeValue, setIssueAgeValue] = useState("");
  const [issueAgeNewValue, setIssueAgeNewValue] = useState("");
  const [uplineIds, setUplineIds] = useState([]);
  const [effectiveStartDate, setEffectiveStartDate] = useState("");
  const [effectiveEndDate, setEffectiveEndDate] = useState("");

  const [agentCommissionPercent, setAgentCommissionPercent] = useState("");
  const [houseCommissionPercent, setHouseCommissionPercent] = useState("");
  const [agentAdvMonths, setAgentAdvMonths] = useState("");
  const [houseAdvMonths, setHouseAdvMonths] = useState("");

  useEffect(() => {
    if (!open) return;
    (async () => {
      const res = await api.get("/commission/companies");
      setCompanies(res.data || []);
    })();
    (async () => {
      const res = await api.get("/commission/agents");
      setAgents(res.data || []);
    })();
    (async () => {
      const res = await api.get("/admin/users");
      const eligible = (res.data || []).filter((u) =>
        ["agent", "admin", "super_admin"].includes(u.role) && u.enabled !== false
      );
      setUplines(eligible);
    })();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!companyId) {
      setProducts([]);
      return;
    }
    (async () => {
      const res = await api.get(`/commission/companies/${companyId}/products`);
      setProducts(res.data || []);
    })();
  }, [companyId, open]);


  useEffect(() => {
    if (!open || !row) return;
    setAgentId(row.agent_id || "");
    setCompanyId(row.company_id || "");
    setProductId(row.product_id || "");
    setIssueAgeValue(row.issue_age || "");
    setIssueAgeNewValue("");
    setEffectiveStartDate(row.effective_start_date || "");
    setEffectiveEndDate(row.effective_end_date || "");
    setUplineIds(row.upline_ids || []);

    setAgentCommissionPercent(row.default_agent_commission_percent ?? "");
    setHouseCommissionPercent(row.house_commission_percent ?? "");
    setAgentAdvMonths(row.default_agent_advance_months ?? "");
    setHouseAdvMonths(row.house_advance_months ?? "");
  }, [open, row]);

  const companyOptions = useMemo(() => (companies || []).map((c) => ({ value: c.id, label: c.name })), [companies]);
  const productOptions = useMemo(() => (products || []).map((p) => ({ value: p.id, label: p.name })), [products]);

  const agentOptions = useMemo(() => {
    return (agents || [])
      .map((a) => {
        const name = String(a.name || "").trim();
        const email = String(a.email || "").trim();
        const label = name && email ? `${name} (${email})` : name || email || a.id;
        return { value: a.id, label };
      })
      .sort((x, y) => String(x.label).localeCompare(String(y.label), undefined, { sensitivity: "base" }));
  }, [agents]);

  const uplineOptions = useMemo(() => {
    return (uplines || [])
      .map((u) => {
        const name = String(u.name || "").trim();
        const email = String(u.email || "").trim();
        const label = name && email ? `${name} (${email})` : name || email || u.id;
        return { value: u.id, label };
      })
      .sort((x, y) => String(x.label).localeCompare(String(y.label), undefined, { sensitivity: "base" }));
  }, [uplines]);

  const issueAgeOptions = useMemo(() => {
    const values = new Set();
    if (companyId && productId) {
      (existingProfiles || []).forEach((p) => {
        if (p.company_id === companyId && p.product_id === productId && p.issue_age) {
          values.add(String(p.issue_age));
        }
      });
    }
    if (issueAgeValue && issueAgeValue !== "__new__") {
      values.add(String(issueAgeValue));
    }
    const opts = Array.from(values)
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }))
      .map((v) => ({ value: v, label: v }));
    return [{ value: "__new__", label: "+ New" }, ...opts];
  }, [companyId, productId, existingProfiles, issueAgeValue]);

  const uplineLabels = useMemo(() => {
    const byId = {};
    (uplineOptions || []).forEach((u) => (byId[u.value] = u.label));
    return uplineIds.map((id) => byId[id]).filter(Boolean);
  }, [uplineIds, uplineOptions]);

  const uplineIdsByLabel = useMemo(() => {
    const m = new Map();
    (uplineOptions || []).forEach((u) => m.set(u.label, u.value));
    return m;
  }, [uplineOptions]);
  const issueAgeText = issueAgeValue === "__new__" ? issueAgeNewValue.trim() : issueAgeValue.trim();
  const canSelectCompany = Boolean(agentId);
  const canSelectProduct = Boolean(companyId);
  const canSelectIssueAge = Boolean(productId);
  const canSelectUpline = Boolean(issueAgeText);
  const canSave = Boolean(companyId && productId && issueAgeText && effectiveStartDate);

  const save = async () => {
    if (!row?.id) return;
    setBusy(true);
    try {
      const res = await api.put(`/commission/profiles/${row.id}`, {
        company_id: companyId,
        product_id: productId,
        issue_age: issueAgeText,
        upline_ids: uplineIds,
        effective_start_date: effectiveStartDate,
        effective_end_date: effectiveEndDate || null,
        default_agent_commission_percent: numOrNull(agentCommissionPercent),
        house_commission_percent: numOrNull(houseCommissionPercent),
        default_agent_advance_months: numOrNull(agentAdvMonths),
        house_advance_months: numOrNull(houseAdvMonths),
      });
      
      // Show recalculation feedback
      const recalculatedCount = res.data?.recalculated_clients_count || 0;
      if (recalculatedCount > 0) {
        toast({
          title: "Commission Profile Updated",
          description: `Recalculated commissions for ${recalculatedCount} client${recalculatedCount !== 1 ? 's' : ''}.`,
        });
      } else {
        toast({
          title: "Commission Profile Updated",
          description: "No clients were affected by this change.",
        });
      }
      
      onSaved?.();
    } catch (e) {
      toast({
        title: "Could not save",
        description: e?.response?.data?.detail || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="glass max-h-[85vh] w-[95vw] overflow-y-auto border-white/10 text-slate-100 sm:max-w-[620px]"
        data-testid="cp-edit-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-base">Edit Profile</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1">
            <div className="text-sm font-semibold">Sales Agent</div>
            <SingleSelectFilter
              placeholder="Select agent"
              options={agentOptions}
              value={agentId}
              onChange={(v) => setAgentId(v)}
              testIdPrefix="cp-edit-agent"
              disabled
            />
          </div>

          <div className="space-y-1">
            <div className="text-sm font-semibold">Company</div>
            <SingleSelectFilter
              placeholder="Choose"
              options={companyOptions}
              value={companyId}
              onChange={(v) => {
                setCompanyId(v);
                setProductId("");
                setIssueAgeValue("");
                setIssueAgeNewValue("");
              }}
              testIdPrefix="cp-edit-company"
              disabled={!canSelectCompany}
            />
          </div>

          <div className="space-y-1">
            <div className="text-sm font-semibold">Product</div>
            <SingleSelectFilter
              placeholder="Choose"
              options={productOptions}
              value={productId}
              onChange={(v) => {
                setProductId(v);
                setIssueAgeValue("");
                setIssueAgeNewValue("");
              }}
              testIdPrefix="cp-edit-product"
              disabled={!canSelectProduct}
            />
          </div>

          <div className="space-y-1">
            <div className="text-sm font-semibold">Issue Age</div>
            <SingleSelectFilter
              placeholder="Choose issue age"
              options={issueAgeOptions}
              value={issueAgeValue}
              onChange={(v) => {
                setIssueAgeValue(v);
                if (v !== "__new__") setIssueAgeNewValue("");
              }}
              testIdPrefix="cp-edit-issue-age"
              disabled={!canSelectIssueAge}
            />
            {issueAgeValue === "__new__" ? (
              <Input
                value={issueAgeNewValue}
                onChange={(e) => setIssueAgeNewValue(e.target.value)}
                className="mt-2 bg-white/5 ring-1 ring-white/10"
                placeholder="New issue age"
                data-testid="cp-edit-issue-age-new"
              />
            ) : null}
          </div>

          <div className="space-y-1">
            <div className="text-sm font-semibold">Set Upline</div>
            <MultiSelectFilter
              placeholder="Select upline"
              options={uplineOptions.map((u) => u.label)}
              values={uplineLabels}
              onChange={(labels) => {
                setUplineIds(labels.map((x) => uplineIdsByLabel.get(x)).filter(Boolean));
              }}
              testIdPrefix="cp-edit-upline"
              disabled={!canSelectUpline}
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <div className="text-sm font-semibold">Profile Start Date</div>
              <Input
                type="date"
                value={effectiveStartDate}
                onChange={(e) => setEffectiveStartDate(e.target.value)}
                className="bg-white/5 ring-1 ring-white/10"
                data-testid="cp-edit-effective-start"
              />
            </div>
            <div className="space-y-1">
              <div className="text-sm font-semibold">Profile End Date</div>
              <Input
                type="date"
                value={effectiveEndDate}
                onChange={(e) => setEffectiveEndDate(e.target.value)}
                className="bg-white/5 ring-1 ring-white/10"
                data-testid="cp-edit-effective-end"
              />
            </div>

            <div className="space-y-1">
              <div className="text-sm font-semibold">Agent Commission %</div>
              <Input
                value={agentCommissionPercent}
                onChange={(e) => setAgentCommissionPercent(e.target.value)}
                className="bg-white/5 ring-1 ring-white/10"
                data-testid="cp-edit-agent-commission"
              />
            </div>
            <div className="space-y-1">
              <div className="text-sm font-semibold">House Commission %</div>
              <Input
                value={houseCommissionPercent}
                onChange={(e) => setHouseCommissionPercent(e.target.value)}
                className="bg-white/5 ring-1 ring-white/10"
                data-testid="cp-edit-house-commission"
              />
            </div>
            <div className="space-y-1">
              <div className="text-sm font-semibold">Agent Adv Months</div>
              <Input
                value={agentAdvMonths}
                onChange={(e) => setAgentAdvMonths(e.target.value)}
                className="bg-white/5 ring-1 ring-white/10"
                data-testid="cp-edit-agent-adv"
              />
            </div>
            <div className="space-y-1">
              <div className="text-sm font-semibold">House Adv Months</div>
              <Input
                value={houseAdvMonths}
                onChange={(e) => setHouseAdvMonths(e.target.value)}
                className="bg-white/5 ring-1 ring-white/10"
                data-testid="cp-edit-house-adv"
              />
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            disabled={!canSave || busy}
            onClick={save}
            data-testid="cp-edit-save"
          >
            {busy ? "Saving & Recalculating..." : "Save"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
            data-testid="cp-edit-cancel"
          >
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
